import { StatesMachine } from "components/pipeline-components/classes/state-machine/state-machine";
import { StateProps } from "components/pipeline-components/classes/state-machine/state";
import { BlockItem } from "./types";
import { applyPreConfigToMachine, buildPreConfigByOperation } from "./helpers";

export interface ModeloMachineState {
  machine: StatesMachine;
  orderedOps: StateProps["operation"][];
}

function getOrderedOps(machine: StatesMachine): StateProps["operation"][] {
  return machine
    .getVisibleStates()
    .filter((s) => !["START", "END"].includes(s.operation))
    .map((s) => s.operation);
}

export type ModeloMachineAction =
  | {
      type: "MUTATE";
      updater: (machine: StatesMachine) => StatesMachine | void;
      orderedOps?: StateProps["operation"][];
    }
  | { type: "ADD_BLOCK"; operation: StateProps["operation"] }
  | { type: "REMOVE_BLOCK"; index: number }
  | { type: "REORDER_BLOCK"; from: number; to: number }
  | {
      type: "UPDATE_PRE_CONFIG";
      operation: StateProps["operation"];
      preConfig: Partial<StateProps>;
    };

export function initModeloMachine(sequenciaBloco?: string): ModeloMachineState {
  if (!sequenciaBloco) {
    return { machine: new StatesMachine({ states: [] }), orderedOps: [] };
  }
  try {
    const machine = new StatesMachine(JSON.parse(sequenciaBloco));
    const orderedOps = getOrderedOps(machine);
    return { machine, orderedOps };
  } catch {
    return { machine: new StatesMachine({ states: [] }), orderedOps: [] };
  }
}

export function getBlocksFromMachine(
  state: ModeloMachineState,
  expandedOps: Set<StateProps["operation"]>
): BlockItem[] {
  return state.orderedOps.map((op) => ({
    operation: op,
    preConfig: buildPreConfigByOperation(state.machine, op),
    expanded: expandedOps.has(op)
  }));
}

function rebuildConnections(
  machine: StatesMachine,
  orderedOps: StateProps["operation"][]
) {
  for (const state of machine.states) {
    state.transitions = undefined;
  }

  const start = machine.states.find((s) => s.operation === "START");
  const end = machine.states.find((s) => s.operation === "END");
  if (!start || !end) return;

  const visibleInOrder = orderedOps
    .map((op) => machine.getVisibleStates().find((s) => s.operation === op))
    .filter((s): s is NonNullable<typeof s> => !!s);

  if (visibleInOrder.length > 0) {
    machine.connectStates(start.id, visibleInOrder[0].id);
    for (let i = 0; i < visibleInOrder.length - 1; i++) {
      machine.connectStates(visibleInOrder[i].id, visibleInOrder[i + 1].id);
    }
    machine.connectStates(visibleInOrder[visibleInOrder.length - 1].id, end.id);
  } else {
    machine.connectStates(start.id, end.id);
  }
}

function mutateModeloMachine(
  state: ModeloMachineState,
  updateMachine: (draft: StatesMachine) => StatesMachine | void,
  nextOps: StateProps["operation"][] = state.orderedOps
): ModeloMachineState {
  const draft = state.machine.clone();
  const updatedMachine = updateMachine(draft);
  const nextMachine = updatedMachine ?? draft;

  rebuildConnections(nextMachine, nextOps);
  nextMachine.syncTransitionsBySequence();
  return { machine: nextMachine, orderedOps: nextOps };
}

export function modeloMachineReducer(
  state: ModeloMachineState,
  action: ModeloMachineAction
): ModeloMachineState {
  switch (action.type) {
    case "MUTATE": {
      return mutateModeloMachine(
        state,
        action.updater,
        action.orderedOps ?? state.orderedOps
      );
    }

    case "ADD_BLOCK": {
      if (state.orderedOps.includes(action.operation)) return state;

      const nextOps = [...state.orderedOps, action.operation];
      return mutateModeloMachine(
        state,
        (draft) => {
          draft.addOperationState(action.operation);
        },
        nextOps
      );
    }

    case "REMOVE_BLOCK": {
      const op = state.orderedOps[action.index];
      if (!op) return state;

      const nextOps = state.orderedOps.filter((_, i) => i !== action.index);
      return mutateModeloMachine(
        state,
        (draft) => {
          draft.removeOperation(op);
        },
        nextOps
      );
    }

    case "REORDER_BLOCK": {
      if (
        action.from === action.to ||
        action.from < 0 ||
        action.to < 0 ||
        action.from >= state.orderedOps.length ||
        action.to >= state.orderedOps.length
      ) {
        return state;
      }

      const nextOps = [...state.orderedOps];
      const [item] = nextOps.splice(action.from, 1);
      if (!item) return state;
      nextOps.splice(action.to, 0, item);

      return mutateModeloMachine(state, () => undefined, nextOps);
    }

    case "UPDATE_PRE_CONFIG": {
      return mutateModeloMachine(state, (draft) => {
        const mergedPreConfig = {
          ...buildPreConfigByOperation(draft, action.operation),
          ...action.preConfig
        };

        applyPreConfigToMachine(draft, {
          operation: action.operation,
          preConfig: mergedPreConfig,
          expanded: false
        });
      });
    }

    default:
      return state;
  }
}
